import type { WerkcurveOutput } from '@/lib/interview/types';
import { QUADRANTS } from '@/lib/interview/outputs/werkcurve-levels';

const EFFORT_LABEL = { klein: 'Kleine inspanning', middel: 'Middelgrote inspanning', groot: 'Grote inspanning' } as const;

const FREQUENCY_LABEL: Record<WerkcurveOutput['use_cases'][number]['frequency'], string> = {
  dagelijks: 'Dagelijks',
  wekelijks: 'Wekelijks',
  maandelijks: 'Maandelijks',
  onbekend: 'Frequentie onbekend',
};

export function UseCasesTab({ werkcurve: w }: { werkcurve: WerkcurveOutput }) {
  return (
    <div className="space-y-4">
      <p className="text-off-black/60">
        Werk uit het gesprek waar AI het meeste kan doen. Wat vaak terugkomt en waar AI zelf het werk overneemt,
        staat bovenaan.
      </p>
      {w.use_cases.map((u, i) => {
        const recommended = w.recommendation?.title === u.title;
        return (
        <article
          key={i}
          className={`bg-white rounded-2xl p-6 border ${recommended ? 'border-orange-cta/50' : 'border-off-black/10'}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="font-display font-light text-2xl text-off-black">{u.title}</h2>
            <div className="flex flex-wrap gap-1.5">
              {recommended && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-off-black text-off-white">Hier beginnen</span>
              )}
              {u.is_leap && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-cta text-white">De sprong</span>
              )}
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-off-black/5 text-off-black/70">
                {QUADRANTS[u.quadrant].name}
              </span>
            </div>
          </div>
          <p className="text-off-black/80 mt-2">{u.work}</p>
          <p className="text-sm text-off-black/50 mt-1">
            {FREQUENCY_LABEL[u.frequency]}
            {u.effort ? ` · ${EFFORT_LABEL[u.effort]}` : ''} · {QUADRANTS[u.quadrant].meaning}
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-1">Waarom dit</p>
              <p className="text-sm text-off-black/70">{u.why}</p>
            </div>
            {u.prerequisites.length > 0 && (
              <div>
                <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-1">Wat er moet kloppen</p>
                <ul className="space-y-1">
                  {u.prerequisites.map((p, j) => (
                    <li key={j} className="text-sm text-off-black/70 flex gap-2">
                      <span className="text-orange-cta">&#8226;</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {u.outcome && (
              <div>
                <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-1">Wat het oplevert</p>
                <p className="text-sm text-off-black/70">{u.outcome}</p>
              </div>
            )}
            {u.success_signal && (
              <div>
                <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-1">Hoe je weet dat het werkt</p>
                <p className="text-sm text-off-black/70">{u.success_signal}</p>
              </div>
            )}
          </div>
          {u.first_steps && u.first_steps.length > 0 && (
            <div className="mt-5 pt-5 border-t border-off-black/10">
              <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-2">De eerste 30 dagen</p>
              <ol className="space-y-1.5">
                {u.first_steps.map((s, j) => (
                  <li key={j} className="text-sm text-off-black/70 flex gap-2">
                    <span className="text-orange-cta font-medium">{j + 1}.</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </article>
        );
      })}
    </div>
  );
}
